import React, { memo, ReactNode } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import {
  AvatarDiv,
  CheckMarkIcon,
  Description,
  DescriptionDiv,
  DropDiv,
  Item,
  ItemMainDiv,
  Label,
  Suffix,
  Autotext
} from './styles';
import Avatar from '../../avatar';
import { AVATAR_BORDER, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, ICON_SIZE } from '../../../global/constants';
import IconButton from '../../button/iconButton';
import CheckboxInput from '../../input/checkboxInput';
import SwitchInput from '../../input/switchInput';

interface DropdownItemProps {
  onClick?: (event?: React.MouseEvent, value?: number | string) => void;
  children?: React.ReactNode;
  label?: string;
  iconName?: string;
  iconSize?: number;
  isSuffix?: string | ReactNode; //label text at last in dropdown
  description?: string;
  tone?: 'primary' | 'critical'; //color of label
  isCheckBox?: boolean;
  justifyAlign?: 'center' | 'spaceBetween'; //alignment of label
  align?: 'center' | 'flex-start'; // item align
  isAvatar?: boolean; //condition to show avatar
  avatarSrc?: string;
  isSuffixCross?: boolean; //hover on label close icon show
  isSelected?: boolean; //label selected
  isChecked?: boolean; //checkbox selected state
  iconViewBox?: string; // viewBox for icon if different from default 0 0 16 16
  iconTone?: 'fill' | 'stroke';
  isActive?: boolean;
  onSuffixClick?: () => void; //onclick of cross icon
  isSlider?: boolean;
  isCalenderDropdown?: boolean;
  isSnoozeDropdown?: boolean;
  customIcon?: React.ReactNode;
  isSuffixIcon?: boolean;
}

const DropdownItem = memo(
  ({
    onClick,
    children,
    label,
    iconName,
    iconSize = 16,
    isSuffix,
    description,
    tone = 'primary',
    isCheckBox = false,
    justifyAlign = 'spaceBetween',
    align = 'center',
    isAvatar = false,
    avatarSrc,
    isSuffixCross = false,
    isSelected = false,
    isChecked = false,
    iconViewBox = `0 0 ${iconSize} ${iconSize}`,
    iconTone = 'fill',
    isActive = false,
    onSuffixClick,
    isSlider,
    isCalenderDropdown,
    isSnoozeDropdown,
    customIcon,
    isSuffixIcon
  }: DropdownItemProps) => {
    return (
      <ItemMainDiv className='reference-item'>
        <Item
          role='button'
          isActive={isActive}
          isCalenderDropdown={isCalenderDropdown}
          className='dropdown-item'
          onClick={(e: React.MouseEvent) => onClick && onClick(e)}
          justifyAlign={justifyAlign}
          iconTone={iconTone}
          isSuffixIcon={isSuffixIcon}
          tone={tone}>
          <DropDiv align={align}>
            {children}
            {isCheckBox && (
              <>
                <CheckboxInput
                  onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                    e.stopPropagation();
                    onClick?.(e);
                  }}
                  checked={isChecked}
                  checkboxType='square-checkbox'
                />
              </>
            )}
            {isAvatar && (
              <AvatarDiv>
                <Avatar imgSrc={avatarSrc || ''} name={label || ''} size={20} borderVariant={AVATAR_BORDER.NONE} />
              </AvatarDiv>
            )}
            {iconName && <SVGIcon name={iconName} width={`${iconSize}`} height={`${iconSize}`} viewBox={iconViewBox} />}
            {customIcon && React.isValidElement(customIcon) && customIcon}
            <DescriptionDiv>
              <Label tone={tone}>{label}</Label>
              {description && <Description>{description}</Description>}
            </DescriptionDiv>
          </DropDiv>
          {isSuffix && <Suffix isSnoozeDropdown={isSnoozeDropdown}>{isSuffix}</Suffix>}
          {isSlider && (
            <Autotext>
              <SwitchInput
                checked={isChecked}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.stopPropagation();
                  onClick?.();
                }}
              />
            </Autotext>
          )}
          {isSuffixCross && (
            <IconButton
              variant={ICON_BUTTON_TYPE.TERTIARY}
              size={ICON_BUTTON_SIZE.EXTRA_SMALL}
              iconName='new-close-icon'
              onClick={(e) => onSuffixClick?.()}
              className='close-icon-tag'
            />
          )}
          {isSelected && (
            <CheckMarkIcon>
              <SVGIcon
                name='correct-sign-icon'
                width={ICON_SIZE.LG}
                height={ICON_SIZE.LG}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                fill='var(--text-primary)'
              />
            </CheckMarkIcon>
          )}
        </Item>
      </ItemMainDiv>
    );
  }
);

export default DropdownItem;
